import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import AppModal from "../../../components/AppModal";
import claimsGQL from "../../../gql/claims";
import superAdminGQL from "../../../gql/superAdmin";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import AppButton from "../../../shared/AppButton";
import { useTranslation } from "react-i18next";

function UpdateClaimStatusModal({ open, onClose, claimData, refetchDetails }) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState("");
  const [status, setStatus] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [items, setItems] = useState([]);

  const outcomeOptions = [
    { id: "complete", name: "complete" },
    { id: "error", name: "error" },
    { id: "queued", name: "queued" },
    { id: "partial", name: "partial" },
    { id: "pended", name: "pended" },
  ];

  const statusOptions = [
    { id: "approved", name: "approved" },
    { id: "rejected", name: "rejected" },
    { id: "pended", name: "pended" },
    { id: "pending", name: "pending" },
    { id: "draft", name: "draft" },
    { id: "cancelled", name: "cancelled" },
    { id: "not-required", name: "not-required" },
  ];

  const itemStatusOptions = [
    { id: "approved", name: "approved" },
    { id: "rejected", name: "rejected" },
    { id: "pended", name: "pended" },
    { id: "pending", name: "pending" },
  ];

  // Using superAdminGQL.UPDATE_PRE_AUTH (updatePreAuthStatus) for updating transaction status
  const [updateStatus, { loading: updatingClaim }] = useMutation(
    superAdminGQL.UPDATE_PRE_AUTH
  );

  useEffect(() => {
    if (claimData && open) {
      setOutcome(claimData?.outcome || "");
      setStatus(claimData?.status || "");
      setBundleId(claimData?.request_bundle_id || claimData?.response_bundle_id || "");
      if (Array.isArray(claimData?.items)) {
        setItems(
          claimData.items.map((it) => ({
            id: it?.sequence_no || it?.id,
            sequence_no: it?.sequence_no,
            code: it?.standard_code || it?.non_standard_code || "---",
            description:
              it?.standard_description || it?.non_standard_description || "---",
            status: it?.status || "",
            reject_reason_code: it?.reject_reason_code || "",
            reject_reason_display: it?.reject_reason_display || "",
          }))
        );
      } else {
        setItems([]);
      }
    }
  }, [claimData, open]);

  const handleItemStatusChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleApplyAllItemStatus = (newStatus) => {
    if (!newStatus) return;
    setItems((prev) =>
      prev.map((it) => ({
        ...it,
        status: newStatus,
      }))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const transactionId = claimData?.transaction_id;
    if (!transactionId) {
      toast.error(t("Transaction ID is missing."));
      return;
    }

    const filterItems = items.map((it) => ({
      id: String(it.id || it.sequence_no),
      status: it.status || "",
      reject_reason_code: it.status === "rejected" ? (it.reject_reason_code || "") : "",
      reject_reason_display: it.status === "rejected" ? (it.reject_reason_display || "") : "",
    }));

    const inputData = {
      transaction_id: transactionId,
      outcome: outcome,
      disposition: status,
      items: filterItems,
    };

    try {
      const response = await updateStatus({
        variables: { input: inputData },
      });

      const resMsg =
        response?.data?.updatePreAuthStatus?.message ||
        response?.data?.updateClaimStatus?.message ||
        t("Claim status updated successfully!");

      toast.success(resMsg);
      if (refetchDetails) {
        await refetchDetails();
      }
      onClose();
    } catch (err) {
      console.error("Error updating claim status:", err);
      toast.error(err?.message || t("Failed to update claim status"));
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t("Update Claim Status")}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit} className="space-y-2 py-1">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <CustomFilterInput
            label={t("Outcome")}
            isSelect
            options={outcomeOptions}
            placeholder={`-- ${t("Select Outcome")} --`}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          />

          <CustomFilterInput
            label={t("Status")}
            isSelect
            options={statusOptions}
            placeholder={`-- ${t("Select Status")} --`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />

          <CustomFilterInput
            label={t("Bundle ID")}
            type="text"
            placeholder={t("Enter Bundle ID")}
            value={bundleId}
            onChange={(e) => setBundleId(e.target.value)}
          />
        </div>

        {items.length > 0 && (
          <div className="mt-2 border-t pt-2">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs font-bold text-gray-800">
                {t("Item Status")} ({items.length})
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700">
                  {t("Set All Items To")}
                </span>
                <CustomFilterInput
                  isSelect
                  options={itemStatusOptions}
                  placeholder={`-- ${t("Select")} --`}
                  onChange={(e) => handleApplyAllItemStatus(e.target.value)}
                  className="!min-w-[150px]"
                />
              </div>
            </div>

            <div className="max-h-[220px] overflow-y-auto border border-gray-200 rounded-md p-1.5 space-y-1.5 bg-gray-50">
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="p-1.5 bg-white rounded border border-gray-200 flex flex-col md:flex-row gap-2 justify-between items-start md:items-center text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-bold text-gray-700">
                      #{it.sequence_no || idx + 1}:{" "}
                    </span>
                    <span className="font-semibold text-gray-800">{it.code}</span>{" "}
                    -{" "}
                    <span className="text-gray-600 truncate">
                      {it.description}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <CustomFilterInput
                      isSelect
                      options={itemStatusOptions}
                      placeholder={`-- ${t("Item Status")} --`}
                      value={it.status}
                      onChange={(e) =>
                        handleItemStatusChange(idx, "status", e.target.value)
                      }
                      className="!min-w-[130px]"
                    />

                    {it.status === "rejected" && (
                      <CustomFilterInput
                        type="text"
                        placeholder={t("Reject Reason Code")}
                        value={it.reject_reason_code}
                        onChange={(e) =>
                          handleItemStatusChange(
                            idx,
                            "reject_reason_code",
                            e.target.value
                          )
                        }
                        className="!w-[130px]"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1.5 mt-2 border-t">
          <AppButton
            type="button"
            variant="outlined"
            onClick={onClose}
            label={t("Cancel")}
            className="!border-gray-300 !text-gray-600"
          />
          <AppButton
            type="submit"
            variant="contained"
            isLoading={updatingClaim}
            label={t("Update")}
          />
        </div>
      </form>
    </AppModal>
  );
}

export default UpdateClaimStatusModal;
